import { CheckOutlined } from '@ant-design/icons';
import { VolumeUpIcon } from '@heroicons/react/outline';
import { VolumeOffIcon } from '@heroicons/react/solid';
import { Affix, Dropdown, MenuProps, Slider } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { motion } from 'framer-motion';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

import { ShouldRender } from '../shared/basic/ShouldRender';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import { Endpoints } from '../../network';
import { User } from '../../utils/interfaces/User';
import PauseIcon from '../../../assets/Icons/PauseIcon';
import PlayIcon from '../../../assets/Icons/PlayIcon';
import SecBackward from '../../../assets/Icons/SecBackward';
import SecForward from '../../../assets/Icons/SecForward';
import Star from '../../../assets/Icons/Star';

const AudioPlayer: React.FC<{
  bookId: string;
  token: string;
  chapterPageNumber?: number;
  timestamps?: { page: number; start: number; end: number }[];
  showAudio: boolean;
  setPageNumber: (page: number) => void;
  setSeekTime: (page: number) => void;
  seekTime: number;
  pageOffset: number;
  sideBySide: boolean;
  bookImage: string;
  currentChapter: {
    _id: string;
    name: string;
    chapterNumber: number;
    description: string;
    totalPages: number;
    endPage: number;
    startPage: number;
  };
  userData: User;
  onOpenDrawer: () => void;
  setPlaying: (value: boolean) => void;
  playing: boolean;
  setDuration: (value: number) => void;
  duration: number;
  setPlayedSeconds: (value: number) => void;
  playedSeconds: number;
  playerRef: React.RefObject<ReactPlayer>;
  forward15Seconds: () => void;
  backward15Seconds: () => void;
  isSampleBook?: boolean;
}> = ({
  bookId,
  token,
  chapterPageNumber,
  timestamps,
  setPageNumber,
  pageOffset,
  sideBySide,
  setSeekTime,
  seekTime,
  bookImage,
  currentChapter,
  onOpenDrawer,
  userData,
  setPlaying,
  playing,
  setDuration,
  duration,
  setPlayedSeconds,
  playedSeconds,
  playerRef,
  forward15Seconds,
  backward15Seconds,
  isSampleBook = false,
}) => {
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(0.8);

  const hasSeeked = useRef(false);
  const screens = useBreakpoint();
  const router = useRouter();

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    setPlaybackRate(parseFloat(key));
  };
  const baseUrl = process.env.NEXT_PUBLIC_API_READER_URL;
  const audioUrl = isSampleBook
    ? `${baseUrl}/${Endpoints.getSampleAudioBookByBookId_Stream}/${bookId}`
    : `${baseUrl}/${Endpoints.getCompleteAudioBookByBookId_Stream}/${bookId}?token=${encodeURIComponent(token)}`;

  useEffect(() => {
    if (playerRef.current && seekTime > 0) {
      playerRef.current.seekTo(seekTime, 'seconds');
    }
  }, [seekTime]);

  const handleProgress = (state: { playedSeconds: number }) => {
    if (!timestamps) return;

    setPlayedSeconds(state.playedSeconds);
    const currentTime = state.playedSeconds;
    const currentPageData = timestamps.find((item) => {
      const startSec = item.start;
      const endSec = item.end;
      return currentTime >= startSec && currentTime <= endSec;
    });

    if (currentPageData) {
      const newPageNumber = currentPageData.page + pageOffset;

      if (sideBySide) {
        const leftPage =
          newPageNumber % 2 !== 0 ? newPageNumber - 1 : newPageNumber;
        setPageNumber(leftPage);
      } else {
        setPageNumber(newPageNumber);
      }
    }
  };

  const handleAudioEnd = () => {
    console.info('Audio ended, redirecting...');
  };
  const handlePlayPause = () => {
    if (!playing && chapterPageNumber && timestamps) {
      const timestamp = timestamps.find(
        (item) => item.page === chapterPageNumber,
      );
      if (timestamp) {
        setSeekTime(timestamp.start);
      }
    }
    setPlaying(!playing);
  };

  const items: MenuProps['items'] = [
    {
      key: '0.5',
      label: <span>0.5x {playbackRate === 0.5 && <CheckOutlined />}</span>,
    },
    {
      key: '1',
      label: <span>Normal {playbackRate === 1 && <CheckOutlined />}</span>,
    },
    {
      key: '1.5',
      label: <span>1.5x {playbackRate === 1.5 && <CheckOutlined />}</span>,
    },
    {
      key: '2',
      label: <span>2x {playbackRate === 2 && <CheckOutlined />}</span>,
    },
  ];

  return (
    <Affix offsetBottom={0}>
      <motion.div className=' bg-black text-white shadow-lg -z-10'>
        <div className='flex items-center gap-x-5 justify-between pt-2'>
          <ShouldRender check={screens.md}>
            <div className='flex items-center justify-start  w-full '>
              <div
                style={{
                  background:
                    'linear-gradient(180deg, #FFFFFF 0%, #999999 100%)',
                }}
                className='bg-white pt-3 px-5'
              >
                <Image
                  src={bookImage}
                  alt='Cover'
                  width={43}
                  height={63}
                  className='rounded-t'
                />
              </div>
              <ShouldRender check={!isSampleBook}>
                <div className='ml-3'>
                  <p className='text-xs xl:text-sm font-semibold'>
                    {currentChapter.name}
                  </p>
                  <p className='text-[10px] xl:text-xs text-gray-400'>
                    {currentChapter.startPage} to {currentChapter.endPage} Pages
                  </p>
                </div>
              </ShouldRender>
            </div>
          </ShouldRender>

          <div className='flex flex-col px-4 py-3  w-full md:py-1'>
            <div className='flex items-center relative justify-center gap-x-6 w-full'>
              <Dropdown
                menu={{ items, onClick: handleMenuClick }}
                placement='top'
                arrow={false}
              >
                <span className='text-xs font-bold absolute left-0 text-primary cursor-pointer'>
                  {playbackRate}x
                </span>
              </Dropdown>
              <ShouldRender check={!screens.md}>
                <div
                  onClick={() => {
                    onOpenDrawer();
                    const eventParams: EventDetails = {
                      event_name: 'screen_view',
                      source: 'ask_ai',
                      source_type: 'book',
                      source_id: bookId as string,
                      sub_source: null,
                      sub_source_id: null,
                      unit: 'boolean',
                      value: 1,
                      meta: {
                        click_source: 'books_reading_screen',
                      },
                    };

                    logEvent({
                      eventDetails: eventParams,
                      userData: userData,
                      pathname: router.pathname,
                    });
                  }}
                  className='p-2.5  w-8 h-8 rounded-full absolute -top-4 right-5'
                >
                  <Star />
                </div>
              </ShouldRender>

              <span
                onClick={backward15Seconds}
                className='cursor-pointer text-white hover:text-gray-800'
              >
                <SecForward />
              </span>

              <button
                onClick={handlePlayPause}
                className=' flex justify-center items-center  w-8 h-8  rounded-full bg-white'
              >
                {playing ? (
                  <PauseIcon />
                ) : (
                  <span className='ml-0.5'>
                    <PlayIcon />
                  </span>
                )}
              </button>
              <div onClick={forward15Seconds} className='cursor-pointer'>
                <SecBackward />
              </div>
            </div>
            <div className='flex gap-x-2 items-center'>
              <span className='text-xs'>
                {moment.utc(playedSeconds * 1000).format('HH:mm:ss')}
              </span>
              <div className='flex justify-center customSlider w-full mt-2 md:mt-1'>
                <Slider
                  defaultValue={(playedSeconds / duration) * 100 || 0}
                  value={(playedSeconds / duration) * 100 || 0}
                  onChange={(percent) => {
                    const newTime = (percent / 100) * duration;
                    setPlayedSeconds(newTime);
                    if (playerRef.current) {
                      playerRef.current.seekTo(newTime, 'seconds');
                    }
                  }}
                  min={0}
                  max={100}
                  className='mt-1.5 w-full customSlider'
                />
              </div>
              <span className='text-xs'>
                {moment.utc(duration * 1000).format('HH:mm:ss')}
              </span>
            </div>
          </div>

          {/* Volume Control */}
          <ShouldRender check={screens.md}>
            <div className='w-full flex pr-8 justify-end'>
              <div className='flex items-center gap-x-4 customSliderVolume'>
                {volume === 0 ? (
                  <VolumeOffIcon className='h-6 w-6 text-white' />
                ) : (
                  <VolumeUpIcon
                    onClick={() => {
                      setVolume(0);
                    }}
                    className='h-6 w-6 text-white cursor-pointer'
                  />
                )}

                <Slider
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={(value) => setVolume(value)}
                  tooltip={{ formatter: null }}
                  className='w-20 customSliderVolume z-30'
                />
              </div>
            </div>
          </ShouldRender>
        </div>

        <ReactPlayer
          ref={playerRef}
          url={audioUrl}
          playing={playing}
          controls
          width='0px'
          height='0px'
          playbackRate={playbackRate}
          volume={volume}
          onProgress={handleProgress}
          onReady={() => {
            if (playerRef.current && seekTime > 0 && !hasSeeked.current) {
              playerRef.current.seekTo(seekTime, 'seconds');
              hasSeeked.current = true;
            }
          }}
          onContextMenu={(e: any) => e.preventDefault()}
          config={{
            file: {
              attributes: {
                controlsList: 'nodownload',
                disablePictureInPicture: true,
              },
            },
          }}
          onDuration={(duration) => setDuration(duration)}
          onEnded={handleAudioEnd}
        />
      </motion.div>
    </Affix>
  );
};

export default AudioPlayer;
